import { useEffect } from "preact/hooks";
import { GameMode, RankedState } from "../../api/types";
import { refreshMe, requireMe } from "../../state/auth";
import {
  cancelRankedSearch,
  rankedBusy,
  rankedMode,
  rankedStatus,
  rankedTeamSize,
  resumeRankedSearch,
  startRankedSearch,
} from "../../state/matchmaking";
import { requireMeta } from "../../state/meta";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { OptionCard } from "../components/OptionCard";
import { ScreenFrame } from "../components/ScreenFrame";
import { Spinner } from "../components/Spinner";
import { formatClock, formatInt } from "../format";
import { MODE_BLURBS } from "./modeInfo";

const RANKED_MODES: readonly GameMode[] = [GameMode.DEFUSAL, GameMode.TDM];

export function Ranked() {
  const catalog = requireMeta();
  const profile = requireMe().profile;
  const status = rankedStatus.value;
  const mode = rankedMode.value;
  const busy = rankedBusy.value;
  const teamSize = rankedTeamSize.value;
  const searching = status.state === RankedState.SEARCHING;

  useEffect(() => {
    // The rating shown here must include matches that ended after the player left them.
    void refreshMe().catch(() => undefined);
    void resumeRankedSearch();
    // Leaving the screen (back, Escape, menu) leaves the queue, also when the request that joins or
    // resumes it is still in flight. A found match clears the state first, so this is then a no-op.
    return () => void cancelRankedSearch();
  }, []);

  const tiers = catalog.progression.rankTiers;

  return (
    <ScreenFrame
      title="Ranked"
      kicker="Rated matches"
      footer={
        <>
          <p class="ranked__note">
            Ranked is {teamSize}v{teamSize} with a closed roster. Leaving a ranked match counts as a loss.
          </p>
          {searching ? (
            <Button variant="danger" size="lg" busy={busy} onClick={() => void cancelRankedSearch()} class="ranked__action">
              Cancel search
            </Button>
          ) : (
            <Button variant="primary" size="lg" busy={busy} onClick={() => void startRankedSearch(mode)} class="ranked__action">
              Find match
            </Button>
          )}
        </>
      }
    >
      <div class="ranked">
        <div class="ranked__main">
          <h2 class="ranked__heading">Mode</h2>
          <div class="ranked__modes" role="radiogroup" aria-label="Ranked mode">
            {catalog.modes
              .filter((m) => RANKED_MODES.includes(m.mode))
              .map((m) => (
                <OptionCard
                  key={m.mode}
                  role="radio"
                  selected={mode === m.mode}
                  locked={searching}
                  onClick={() => {
                    rankedMode.value = m.mode;
                  }}
                  class="ranked__mode"
                >
                  <span class="card__title">{m.name}</span>
                  <span class="play__rules">
                    {teamSize}v{teamSize}
                  </span>
                  <span class="card__meta">{MODE_BLURBS[m.mode]}</span>
                </OptionCard>
              ))}
          </div>

          <div class="ranked__search" role="status" aria-live="polite">
            {searching ? (
              <>
                <Spinner size={28} />
                <div class="ranked__clock num">{formatClock(status.searchingSec)}</div>
                <div class="ranked__facts">
                  <span>Searching for a ranked match</span>
                  <span class="muted num">
                    {status.playersInQueue} {status.playersInQueue === 1 ? "player" : "players"} in queue
                    {" · "}rating window ±{status.ratingWindow}
                  </span>
                </div>
              </>
            ) : (
              <p class="muted">Choose a mode and start. Matches are made between players of similar rating.</p>
            )}
          </div>
        </div>

        <aside class="ranked__rank" aria-label="Your rank">
          <h2 class="ranked__heading">Your rank</h2>
          <div class="ranked__rating">
            <span class="ranked__rating-value num">{formatInt(profile.rating)}</span>
            <Badge tone="ink">{profile.rankTier}</Badge>
          </div>
          <ol class="ranked__tiers">
            {tiers.map((t) => (
              <li key={t.name} class={t.name === profile.rankTier ? "is-current" : undefined}>
                <span>{t.name}</span>
                <span class="muted num">{t.minRating > 0 ? `${formatInt(t.minRating)}+` : "Start"}</span>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </ScreenFrame>
  );
}
